// The simulation.
//
// One pure function: `step(state, input) -> state`. No DOM, no clock,
// no randomness that isn't seeded. Given the same starting state and
// the same input stream you get the same run, on any machine, in any
// browser, forever — which is what makes replays, ghosts, the daily
// seed and speedrunning possible at all.
//
// The old system had no game loop: state advanced through eight
// intervals, forty-odd timeouts and two rAF sites, all on wall-clock,
// with hit detection living inside timer callbacks. That is why almost
// none of it could be tested. This can be tested entirely without a
// browser.

import { angleT, clamp, deltaT, dirT, dist, makeRng, rngFromSeed, type Rng } from "./rng";
import { emit, PATTERNS, patternById, type EmitStep, type Pattern } from "./patterns";
import {
  applyModifiers,
  applyModifiersInt,
  privAfterHit,
  rollOffers,
  sigtermPromotes,
  type Hook,
  type ModContext,
} from "./upgrades";
import {
  arenaBounds,
  arenaTarget,
  daemonPos,
  DEVICE,
  deviceOn,
  forkSlots,
  handlerOpen,
  phaseAt,
  phaseClock,
  phaseProgress,
  planCards,
  type ArenaBounds,
} from "./phases";
import {
  ARENA_H,
  ARENA_W,
  GRAZE_R,
  NO_SPAWN_R,
  PLAYER_R,
  TICK_HZ,
  type Bullet,
  type Child,
  type RunConfig,
  type SignalDef,
  type SignalId,
  type SimEvent,
  type SimInput,
  type SimState,
  type Zombie,
} from "./types";

// ── Signals — the player's verbs ──────────────────────────────────────

export const SIGNALS: SignalDef[] = [
  {
    id: "sigterm",
    name: "SIGTERM",
    cost: 0,
    // 24 ticks, not 9. See SIGTERM_DAMAGE — the gate is what stops
    // mashing from deleting the phase that teaches you how to play.
    cooldown: 24,
    blurb: "Ask it politely to stop. It declines, but it costs you nothing to ask.",
  },
  {
    id: "sigstop",
    name: "SIGSTOP",
    cost: 2,
    cooldown: 300,
    blurb: "Freeze every projectile for 0.8s. Not invulnerability — be somewhere survivable when it releases.",
  },
  {
    id: "sigkill",
    name: "SIGKILL",
    cost: 5,
    cooldown: 420,
    blurb: "Cannot be caught, cannot be ignored. Heavy damage.",
  },
  {
    id: "renice",
    name: "renice",
    cost: 3,
    cooldown: 360,
    blurb: "Halve its attack cadence for four seconds.",
  },
  {
    id: "strace",
    name: "strace",
    cost: 1,
    cooldown: 180,
    blurb: "Read the next two emissions before they happen.",
  },
];

const signalIndex = (id: SignalId): number => SIGNALS.findIndex((s) => s.id === id);

// ── Tuning ────────────────────────────────────────────────────────────

/** Telegraph lead, in ticks. 30 ticks = 500ms.
 *
 *  The old value was 240ms, which is below the human floor: simple
 *  visual reaction is ~250ms for a prepared subject with nothing to
 *  decide, and a player who must also choose *where to move* is at
 *  350-500ms. 240ms was not a tell, it was a tax on cursors that
 *  weren't already moving. This is also a difficulty axis — see
 *  `niceScale` — rather than a constant. */
export const TELEGRAPH_TICKS = 30;

/**
 * SIGTERM is deliberately pathetic, and that is both the joke and the
 * mechanic. You are asking a process to please stop. It does not care.
 *
 * The numbers matter more than they look. At the original 6 damage per
 * 9 ticks — 40 dmg/s against a ~99 HP card band — a player could delete
 * runlevel 5 in about five seconds by mashing, which meant the *opening
 * card never played*. That card is the tutorial: it is where dodging,
 * then grazing, then signals are taught, and skipping it produces a
 * player who reaches runlevel 3 having learned nothing.
 *
 * At 2 damage per 24 ticks (5 dmg/s) a pure-spam run finishes DAEMON at
 * roughly the 197-second mark, against ~240 seconds of total card
 * timeouts — so mashing is *viable but slow*, and grazing into SIGKILL
 * is how you actually go fast. That gap is the entire skill expression
 * and the whole reason the speedrun categories mean anything.
 *
 * The slower cadence is also a feature: a 24-tick gate makes mashing
 * pointless, which is the behaviour the rebuild exists to kill.
 */
export const SIGTERM_DAMAGE = 2;
export const SIGKILL_DAMAGE = 90;

/** Ticks of invulnerability after taking a hit. Enough to escape the
 *  cluster that hit you, not enough to tank through a card. */
const IFRAMES = 42;

// ── Score: the load average ───────────────────────────────────────────
//
// A second way to be good at this game, deliberately opposed to the
// first.
//
// A card clears on `daemon.hp <= floor || cardTicks <= 0` — either you
// burn it down or you outlast it. That single line is what makes two
// honest rankings possible from one run: spend PRIV on signals and
// clear early (fast), or hoard it, stay inside the bullets and let the
// card run its length (dangerous). Every few seconds "do I spend this?"
// is a real question, and it has two right answers.
//
// Three properties this has to have, and all three fall out of the
// existing sim rather than needing new machinery:
//
//   1. **You cannot farm.** A bullet grazes once (`b.grazed`) and cards
//      time out, so the run has a hard maximum length. Score is bounded
//      without a single anti-milking rule.
//   2. **It must measure *sustained* danger, not luck.** Hence a decay:
//      load bleeds down every tick, so a lucky brush is worth little
//      and thirty seconds inside the pattern is worth a great deal.
//   3. **It must be losable.** Score that only goes up is not danger,
//      it is bookkeeping. A hit collapses load to base, so a long clean
//      streak is genuinely precious and a mistake is genuinely felt.
//
// Held in thousandths as integers. ECMAScript specifies +, - and * on
// doubles exactly, so floats would in fact reproduce — but the run is
// re-simulated on a server to be believed, and integers make that
// argument without anyone having to make it.

/** Idle load. `uptime` on a quiet box, and the floor a hit drops to. */
const LOAD_BASE = 1000;
/** Added per graze. ~12 grazes of sustained contact to reach the cap. */
const LOAD_PER_GRAZE = 80;
/** Ceiling, so a perfect run is bounded and readable. `load 8.00`. */
const LOAD_CAP = 8000;
/** Bled per tick — 1.20 load per second. From the cap, about six
 *  seconds of safety erases everything the danger bought. */
const LOAD_DECAY = 20;
/** Points per graze, before load. */
const SCORE_PER_GRAZE = 10;
/**
 * Points per unit of PRIV the cap threw away, before load.
 *
 * Grazing at full PRIV currently discards it outright — the telemetry
 * counts it as `privOverflow` and nothing else ever sees it. Paying for
 * it turns "I am full and still in here" from waste into the most
 * committed thing you can do, and gives hoarding PRIV a point beyond
 * refusing to spend it.
 */
const SCORE_PER_OVERFLOW = 25;

/** PRIV per graze. Grazing is the only source, so this number is the
 *  exchange rate between courage and options. */
const PRIV_PER_GRAZE = 1;

const SIGSTOP_TICKS = 48;
const RENICE_TICKS = 240;

/** How difficulty scales the things that matter. Lower `nice` is meaner.
 *  Note that telegraph length is included — being harder means seeing
 *  less warning, never seeing *no* warning. */
export function niceScale(nice: number): {
  telegraph: number;
  bulletSpeed: number;
  daemonHp: number;
} {
  // nice 19 (kindest) -> 1.0, nice -20 (meanest) -> 0.0
  const t = clamp((nice + 20) / 39, 0, 1);
  return {
    telegraph: Math.round(TELEGRAPH_TICKS * (0.62 + 0.55 * t)),
    bulletSpeed: 1.28 - 0.36 * t,
    daemonHp: 1.45 - 0.55 * t,
  };
}


/**
 * Route a base value through the drafted upgrades.
 *
 * Every tunable the pool can touch goes through here rather than being
 * read raw, which is the whole reason 28 upgrades yield combinatorial
 * builds instead of 28 isolated effects: they all modify one shared
 * pipeline, so any two of them interact by construction.
 */
function modCtx(s: SimState, signal?: SignalId): ModContext {
  // Projected rather than passed wholesale: `upgrades.ts` deliberately
  // knows nothing about SimState, which is what lets the pool be tested
  // and rebalanced without touching the simulation at all.
  return {
    signal,
    hp: s.player.hp,
    hpMax: s.player.hpMax,
    priv: s.player.priv,
    privMax: s.player.privMax,
    bullets: s.bullets.length,
    children: s.children.length,
    zombies: s.zombies.length,
    phaseIndex: s.phaseIndex,
    sigtermCount: s.stats.sigtermsFired,
    hitsTaken: s.stats.hitsTaken,
    arenaScale: s.arenaScale,
  };
}

function mod(s: SimState, hook: Hook, base: number, signal?: SignalId): number {
  return applyModifiers(s.upgrades, hook, base, modCtx(s, signal));
}

function modInt(s: SimState, hook: Hook, base: number, signal?: SignalId): number {
  return applyModifiersInt(s.upgrades, hook, base, modCtx(s, signal));
}

// ── Construction ──────────────────────────────────────────────────────

export function createRun(config: RunConfig): SimState {
  const rng = rngFromSeed(config.seed);
  const scale = niceScale(config.nice);
  // Drawn before the first tick so a replay never re-derives it, and so
  // the seed genuinely changes the fight rather than only its jitter.
  const cardPlan = planCards(rng);
  const firstCard = cardPlan[0]?.[0];

  return {
    tick: 0,
    status: "intro",
    rng: rng.snapshot(),
    config,
    player: {
      x: ARENA_W / 2,
      y: ARENA_H * 0.78,
      hp: 100,
      hpMax: 100,
      priv: 0,
      privMax: 10,
      loadMilli: LOAD_BASE,
      iframes: 0,
      cooldowns: SIGNALS.map(() => 0),
    },
    daemon: {
      x: ARENA_W / 2,
      y: ARENA_H * 0.26,
      hp: Math.round(900 * scale.daemonHp),
      hpMax: Math.round(900 * scale.daemonHp),
      cooldown: 0,
      patternClock: 0,
      renicedFor: 0,
      step: 0,
      guarded: false,
    },
    phaseIndex: 0,
    cardPlan,
    cardIndex: 0,
    cardTicks: firstCard?.timeout ?? 0,
    bullets: [],
    telegraphs: [],
    children: [],
    zombies: [],
    arenaScale: 1,
    upgrades: [],
    offers: [],
    stats: {
      grazes: 0,
      score: 0,
      peakLoadMilli: LOAD_BASE,
      hitsTaken: 0,
      damageDealt: 0,
      cardsCleared: 0,
      zombiesReaped: 0,
      signalsFired: 0,
      sigtermsFired: 0,
    },
    events: [],
    nextId: 1,
  };
}

// ── The step ──────────────────────────────────────────────────────────

type Ctx = {
  s: SimState;
  rng: Rng;
  ev: SimEvent[];
  /** Previous cursor position, for velocity — the `lead` emitter needs
   *  it and it is cheaper to pass than to store on state. */
  prev: { x: number; y: number };
  /** Phase clock as it stood at the end of the previous tick. Device
   *  schedules are edge crossings between this and the current value,
   *  never equality tests: the clock can leap forward when a card is
   *  cleared on HP rather than on time, and an equality test would
   *  silently drop the fork that leap stepped over. */
  clock0: number;
  clock: number;
  /** The live playfield for this tick. */
  arena: ArenaBounds;
};

/** Did a schedule of "at `first`, then every `period`" fire between two
 *  readings of the phase clock? */
function crossed(prev: number, now: number, first: number, period: number): boolean {
  if (now < first) return false;
  return Math.floor((now - first) / period) !== Math.floor((prev - first) / period);
}

/**
 * Advance the simulation by exactly one tick.
 *
 * Returns a NEW state; the input state is not mutated. That costs a
 * little allocation per tick and buys snapshotting, time-travel
 * debugging and trivially correct replay verification.
 */
export function step(prevState: SimState, input: SimInput, prevInput: SimInput): SimState {
  const s: SimState = {
    ...prevState,
    player: { ...prevState.player, cooldowns: prevState.player.cooldowns.slice() },
    daemon: { ...prevState.daemon },
    stats: { ...prevState.stats },
    events: [],
  };
  const rng = makeRng(s.rng);
  const ctx: Ctx = {
    s,
    rng,
    ev: s.events,
    prev: { x: prevInput.x, y: prevInput.y },
    clock0: phaseClock(prevState),
    clock: 0,
    arena: arenaBounds(prevState.arenaScale),
  };

  s.tick = prevState.tick + 1;

  if (s.status === "won" || s.status === "lost" || s.status === "draft") {
    s.rng = rng.snapshot();
    return s;
  }

  // The intro is a status, not a mode: the first tick of the first
  // phase is where the run formally begins, and that is where the
  // opening `phase-start` / `card-start` pair belongs so the HUD has
  // exactly one code path for "a phase started".
  if (s.status === "intro") {
    s.status = "running";
    const phase = phaseAt(s.phaseIndex);
    s.events.push({ t: "phase-start", phase: phase.id, nice: phase.nice });
    const card = s.cardPlan[s.phaseIndex]?.[s.cardIndex];
    if (card) s.events.push({ t: "card-start", name: card.name });
  }

  tickCardTimer(ctx);
  ctx.clock = phaseClock(s);

  advancePatternClock(ctx);
  movePlayer(ctx, input);
  tickCooldowns(ctx);
  tickLoad(ctx);
  fireSignals(ctx, input);
  // Devices run *after* signals so that a handler window which opens
  // this tick cannot retroactively eat a press the player made before
  // the window was visible.
  runDevices(ctx);
  runPattern(ctx);
  runChildren(ctx);
  moveBullets(ctx);
  resolveGrazeAndHits(ctx);
  tickTelegraphs(ctx);
  advanceCard(ctx);
  checkEnd(ctx);

  s.rng = rng.snapshot();
  return s;
}

/** Burn one tick off the current spell card. Split out of `advanceCard`
 *  so the phase clock is already current when devices schedule against
 *  it, rather than a tick behind for the whole tick. */
function tickCardTimer(ctx: Ctx): void {
  const { s } = ctx;
  if (s.cardTicks > 0) s.cardTicks -= 1;
}

/**
 * Step the clock patterns are read against.
 *
 * Deliberately not `s.tick`: the run timer must keep real time while
 * `renice` slows the fight, or the signal would make every run *look*
 * faster without being faster — and the whole speedrunning apparatus
 * rests on the timer meaning one thing.
 */
function advancePatternClock(ctx: Ctx): void {
  const { s } = ctx;
  if (s.daemon.renicedFor > 0) {
    s.daemon.renicedFor -= 1;
    // Every other tick. Parity is taken from the run tick so the
    // behaviour is identical on replay.
    if (s.tick % 2 === 0) s.daemon.patternClock += 1;
    return;
  }
  s.daemon.patternClock += 1;
}

function movePlayer(ctx: Ctx, input: SimInput): void {
  const { s, arena } = ctx;
  // The cursor is the player. There is no acceleration model on
  // purpose — a mouse has none, and pretending otherwise feels awful.
  // The clamp is to the *live* arena, not the logical one: once the
  // walls start closing the pointer can be outside them, and a player
  // who is outside the arena must be pulled in rather than left in a
  // region the game has stopped defending.
  s.player.x = clamp(input.x, arena.x0 + PLAYER_R, arena.x1 - PLAYER_R);
  s.player.y = clamp(input.y, arena.y0 + PLAYER_R, arena.y1 - PLAYER_R);
}

/**
 * Bleed the load average back toward idle.
 *
 * Unconditional, every tick, including the tick you graze on — a graze
 * adds four ticks' worth, so contact still wins comfortably while
 * standing off does not. Doing it this way rather than "decay only when
 * no graze happened" keeps it independent of collision order, which is
 * one less thing that could make a replay diverge.
 */
function tickLoad(ctx: Ctx): void {
  const p = ctx.s.player;
  if (p.loadMilli > LOAD_BASE) {
    p.loadMilli = Math.max(LOAD_BASE, p.loadMilli - LOAD_DECAY);
  }
}

function tickCooldowns(ctx: Ctx): void {
  const p = ctx.s.player;
  if (signalsLocked(ctx.s)) {
    // Single-user: cooldowns are pinned rather than counted down, so
    // every HUD that already renders "ticks remaining" renders the
    // signal as unavailable without having to know the device exists.
    //
    // Except SIGKILL, which single-user cannot take away — it is not
    // deliverable to a handler and cannot be caught, blocked or
    // ignored. Pinning its cooldown here was the *actual* enforcement
    // of the lock; `signalsLocked` in `fire()` only refuses the press.
    // Exempt one without the other and the signal is still unusable,
    // which is exactly what happened first time round.
    for (let i = 0; i < p.cooldowns.length; i++) {
      const id = SIGNALS[i]?.id;
      if (id === "sigkill") {
        if (p.cooldowns[i]! > 0) p.cooldowns[i] = p.cooldowns[i]! - 1;
        continue;
      }
      p.cooldowns[i] = DEVICE.singleUser.lock;
    }
    if (p.iframes > 0) p.iframes -= 1;
    return;
  }
  for (let i = 0; i < p.cooldowns.length; i++) {
    if (p.cooldowns[i]! > 0) p.cooldowns[i] = p.cooldowns[i]! - 1;
  }
  if (p.iframes > 0) p.iframes -= 1;
}

/**
 * Are the player's verbs available at all?
 *
 * False for the whole of runlevel 1, which is the single-user device:
 * every signal the player drafted, bought and learned is gone and there
 * is only the cursor. Exported because the HUD must be able to say so —
 * a run of silently-failing keypresses reads as a bug, and "you have no
 * signals" is the entire point of the phase.
 */
export const signalsLocked = (s: SimState): boolean =>
  deviceOn(s.phaseIndex, "single-user") && phaseAt(s.phaseIndex).device === "single-user";

/**
 * The one signal single-user mode cannot take away.
 *
 * SIGKILL is not deliverable to a handler. It is not catchable, not
 * blockable, not ignorable — the kernel does it *to* you, and no amount
 * of dropping to single-user changes that. So runlevel 1 strips every
 * verb you have except the one that cannot be refused, which happens
 * also to be the one the game is named after.
 *
 * Before this, the final runlevel took SIGTERM as well and the run was
 * won by outlasting a timer: sixty seconds of dodging with no way to
 * answer, and DAEMON was never actually terminated. For a game whose
 * own any% blurb reads "Terminate DAEMON", never terminating it was a
 * strange note to end on.
 *
 * It still costs the full 5 PRIV. The finale is therefore: survive a
 * pattern you cannot fight back against, graze it for the privilege,
 * and spend it. The timeout clear is untouched, so failing to afford
 * the kill still wins by survival — this adds a way to end it, it does
 * not add a way to lose.
 */
export const signalRefused = (s: SimState, id: SignalId): boolean =>
  signalsLocked(s) && id !== "sigkill";

/** Convenience inverse, for HUD code that reads better in the positive. */
export const signalsAvailable = (s: SimState): boolean => !signalsLocked(s);

/** Index of the child a signal should retarget to, or -1 for DAEMON.
 *
 *  Targeting is positional and automatic: the nearest child within
 *  reach of the cursor takes the hit. That is what makes the fork
 *  trade a real decision — to kill children you must go and stand with
 *  them, in the middle of the board, not damaging the parent. */
function targetChild(s: SimState): number {
  let best = -1;
  let bestD: number = DEVICE.fork.reach;
  for (let i = 0; i < s.children.length; i++) {
    const c = s.children[i]!;
    const d = dist(s.player.x, s.player.y, c.x, c.y);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** Apply damage to a child, and turn it into a zombie if it dies. */
function damageChild(ctx: Ctx, index: number, damage: number, crit: boolean): void {
  const { s, ev } = ctx;
  const c = s.children[index];
  if (!c) return;
  const hp = c.hp - damage;
  s.stats.damageDealt += damage;
  ev.push({ t: "daemon-hit", x: c.x, y: c.y, damage, crit });
  if (hp > 0) {
    s.children = s.children.map((x, i) => (i === index ? { ...x, hp } : x));
    return;
  }
  s.children = s.children.filter((_, i) => i !== index);
  s.zombies = s.zombies.concat({
    id: s.nextId++,
    x: c.x,
    y: c.y,
    reap: DEVICE.zombie.reap,
  });
  ev.push({ t: "child-down", x: c.x, y: c.y });
}

function fireSignals(ctx: Ctx, input: SimInput): void {
  const { s, ev } = ctx;
  const fire = (id: SignalId): void => {
    const idx = signalIndex(id);
    const def = SIGNALS[idx]!;
    if (signalRefused(s, id)) {
      // Refused loudly. The event is the difference between "the game
      // is broken" and "the game has taken something from you".
      ev.push({ t: "signal", id, ok: false });
      return;
    }
    const cost = modInt(s, "signal-cost", def.cost, id);
    if (s.player.cooldowns[idx]! > 0 || s.player.priv < cost) {
      ev.push({ t: "signal", id, ok: false });
      return;
    }
    s.player.priv -= cost;
    s.player.cooldowns[idx] = modInt(s, "signal-cooldown", def.cooldown, id);
    s.stats.signalsFired += 1;
    ev.push({ t: "signal", id, ok: true });

    switch (id) {
      case "sigterm": {
        // Counted before anything else resolves: a SIGTERM that bounces
        // off a handler still *fired*, and `sigqueue(3)` queues presses,
        // not connections. This is also why the counter is its own stat
        // — `signalsFired` is inflated by SIGSTOP/renice/strace and
        // cannot be used to find every fifth polite request.
        const nth = s.stats.sigtermsFired;
        s.stats.sigtermsFired = nth + 1;
        const promoted = sigtermPromotes(s.upgrades, nth);
        const dmg = modInt(
          s,
          "damage-dealt",
          promoted ? SIGKILL_DAMAGE : SIGTERM_DAMAGE,
          "sigterm",
        );

        // Children first, if the cursor is standing with them. A child
        // is a plain process with no handler installed, so the guard
        // does not protect it — which gives the player something to do
        // with a blocked SIGTERM other than wait.
        const target = targetChild(s);
        if (target >= 0) {
          damageChild(ctx, target, dmg, promoted);
          break;
        }
        // Only connects if DAEMON isn't inside a handler window. The
        // guard is what turns spam into timing. A promoted SIGTERM is
        // still a SIGTERM as far as the handler is concerned — the
        // upgrade buys damage, not immunity to the phase's rule.
        if (s.daemon.guarded) break;
        s.daemon.hp = Math.max(0, s.daemon.hp - dmg);
        s.stats.damageDealt += dmg;
        ev.push({ t: "daemon-hit", x: s.daemon.x, y: s.daemon.y, damage: dmg, crit: promoted });
        break;
      }
      case "sigkill": {
        const target = targetChild(s);
        if (target >= 0) {
          damageChild(ctx, target, SIGKILL_DAMAGE, true);
          break;
        }
        s.daemon.hp = Math.max(0, s.daemon.hp - SIGKILL_DAMAGE);
        s.stats.damageDealt += SIGKILL_DAMAGE;
        ev.push({
          t: "daemon-hit",
          x: s.daemon.x,
          y: s.daemon.y,
          damage: SIGKILL_DAMAGE,
          crit: true,
        });
        break;
      }
      case "sigstop": {
        for (const b of s.bullets) b.delay = Math.max(b.delay, SIGSTOP_TICKS);
        break;
      }
      case "renice": {
        // Was `s.daemon.cooldown += RENICE_TICKS / 4` — a write to a
        // field nothing reads, so the signal cost 3 PRIV and did
        // nothing whatsoever. Emissions step off `patternClock`, so
        // halving the cadence means advancing that clock at half rate.
        s.daemon.renicedFor = Math.max(
          s.daemon.renicedFor,
          modInt(s, "signal-power", RENICE_TICKS, "renice"),
        );
        break;
      }
      case "strace":
        // Purely informational; the HUD reads upcoming steps directly.
        break;
    }
  };

  for (const id of input.signals) fire(id);
  if (input.fire) fire("sigterm");
}

// ── Devices ───────────────────────────────────────────────────────────
//
// One rule per phase, and every one of them written as a rule rather
// than a multiplier: a window you can time, a decision with no correct
// answer, a debt you choose whether to collect, a wall that moves, and
// finally the removal of everything you built. None of them make a
// bullet faster.

function runDevices(ctx: Ctx): void {
  handlerDevice(ctx);
  forkDevice(ctx);
  scheduleDevices(ctx, ctx.clock0, ctx.clock);
  zombieDevice(ctx);
  shrinkDevice(ctx);
  moveDaemon(ctx);
  clampActors(ctx);
}

/**
 * Everything a device schedules against the phase clock, for the
 * interval `(from, to]`.
 *
 * It takes an interval rather than reading the clock itself because the
 * clock can leap: clear a card on HP instead of on time and the phase
 * clock jumps to the next card's start. Run this over the skipped
 * interval too (see `advanceCard`) and a fork that was due inside the
 * jump still happens, a tick late, instead of vanishing because nobody
 * was looking.
 */
function scheduleDevices(ctx: Ctx, from: number, to: number): void {
  const { s, rng } = ctx;
  if (to <= from) return;
  const lead = niceScale(s.config.nice).telegraph;

  if (deviceOn(s.phaseIndex, "handlers")) {
    const { period, openAt, telegraphR } = DEVICE.handler;
    if (crossed(from, to, Math.max(0, openAt - lead), period)) {
      s.telegraphs = s.telegraphs.concat({
        id: s.nextId++,
        x: s.daemon.x,
        y: s.daemon.y,
        r: telegraphR,
        ticks: lead,
        kind: "slam",
      });
    }
  }

  if (deviceOn(s.phaseIndex, "fork")) {
    const pending = s.telegraphs.reduce((n, t) => (t.kind === "spawn" ? n + 1 : n), 0);
    const room = DEVICE.fork.max - s.children.length - pending;
    if (room > 0 && crossed(from, to, DEVICE.fork.first, DEVICE.fork.period)) {
      const count = Math.min(DEVICE.fork.count, room);
      s.telegraphs = s.telegraphs.concat(
        forkSlots(ctx.arena, count, rng).map((p) => ({
          id: s.nextId++,
          x: p.x,
          y: p.y,
          r: 70,
          ticks: lead + 12,
          kind: "spawn" as const,
        })),
      );
    }
  }
}

/**
 * runlevel 4 — installed handlers.
 *
 * DAEMON opens a window in which SIGTERM bounces off it. The window is
 * a metronome on the phase clock rather than a die roll, and it is
 * telegraphed before it opens, so the skill it asks for is *timing* —
 * hold the press, spend the dead time grazing — instead of the reflex
 * test a random guard would be. Spam stops working; rhythm starts.
 */
function handlerDevice(ctx: Ctx): void {
  const { s } = ctx;
  if (!deviceOn(s.phaseIndex, "handlers")) {
    s.daemon.guarded = false;
    return;
  }
  // Derived, never stored: the window is a function of the clock, so
  // it cannot drift out of step with the telegraph that announced it.
  s.daemon.guarded = handlerOpen(ctx.clock);
}

/**
 * runlevel 3 — fork.
 *
 * The trade has no correct answer, which is the point:
 *
 *   - Leave the children and the parent takes damage, but two extra
 *     origins are firing at you from the middle of the board.
 *   - Clear the children and you spend the phase standing next to them
 *     while the parent quietly heals whatever you took off it.
 *
 * Children are spawned by their own telegraph expiring rather than on
 * the schedule tick, so the warning and the spawn can never disagree
 * about where — see `tickTelegraphs`.
 */
function forkDevice(ctx: Ctx): void {
  const { s } = ctx;
  if (!deviceOn(s.phaseIndex, "fork")) return;

  // Every live child feeds the parent. This is the cost of ignoring
  // them, and it is a rate rather than a lump so the player can watch
  // the bar creep and decide.
  if (s.children.length > 0) {
    // Capped at the HP it entered the phase with: children can undo
    // this phase's work and no more. The cap only ever heals — a
    // DAEMON already above it (a test fixture, a future upgrade) is
    // left alone rather than quietly drained.
    const ceiling = Math.max(phaseHpFloor(s, 0), s.daemon.hp);
    s.daemon.hp = Math.min(ceiling, s.daemon.hp + DEVICE.fork.healPerChild * s.children.length);
  }
}

/** Pattern index a new child runs. Drawn from tier 1 by tier rather
 *  than by hard-coded index, so the table can grow underneath this
 *  without children suddenly running a boss card. */
function childPatternIndex(rng: Rng): number {
  const simple: number[] = [];
  for (let i = 0; i < PATTERNS.length; i++) if (PATTERNS[i]!.tier === 1) simple.push(i);
  return simple.length > 0 ? rng.pick(simple) : 0;
}

/**
 * Children emit. Their steps come from a tier-1 pattern but are
 * stripped down — fewer bullets, no modifiers, less damage — because
 * two extra full patterns is noise, and the child's job is to make the
 * *parent's* pattern harder to read by coming from somewhere else.
 *
 * Nothing here checks whether the parent is still on the card that
 * spawned them. An orphan keeps firing; that is the whole threat.
 */
function runChildren(ctx: Ctx): void {
  const { s, rng } = ctx;
  if (s.children.length === 0) return;

  const scale = niceScale(s.config.nice);
  const next: Child[] = [];
  let bullets = s.bullets;

  for (const c of s.children) {
    if (c.cooldown > 0) {
      next.push({ ...c, cooldown: c.cooldown - 1 });
      continue;
    }
    const pattern = PATTERNS[c.pattern] ?? PATTERNS[0];
    const steps = pattern?.steps ?? [];
    if (steps.length > 0) {
      const which = Math.floor(s.tick / DEVICE.fork.emitEvery) % steps.length;
      const base = steps[which]!;
      const step: EmitStep = {
        ...base,
        count: Math.min(base.count, DEVICE.fork.maxCount),
        speed: base.speed * DEVICE.fork.speedScale * scale.bulletSpeed,
        kind: "child",
        damage: DEVICE.fork.damage,
        mods: [],
      };
      bullets = bullets.concat(
        emit(
          {
            ox: c.x,
            oy: c.y,
            px: s.player.x,
            py: s.player.y,
            pvx: s.player.x - ctx.prev.x,
            pvy: s.player.y - ctx.prev.y,
            tick: s.tick,
            rng,
            nextId: () => s.nextId++,
          },
          step,
        ),
      );
    }
    next.push({ ...c, cooldown: DEVICE.fork.emitEvery });
  }

  s.children = next;
  s.bullets = bullets;
}

/**
 * Zombies — the greed mechanic.
 *
 * A reaped child leaves a corpse where it stood, which is to say in the
 * middle of the board. It blocks nothing and threatens nothing; all it
 * does is offer PRIV to anyone willing to hold the cursor still, in
 * that spot, for a second and a half. The danger is entirely
 * self-inflicted, which is the best kind.
 */
function zombieDevice(ctx: Ctx): void {
  const { s, ev } = ctx;
  if (s.zombies.length === 0) return;

  const next: Zombie[] = [];
  for (const z of s.zombies) {
    const near = dist(s.player.x, s.player.y, z.x, z.y) <= DEVICE.zombie.range;
    if (near) {
      const reap = z.reap - 1;
      if (reap <= 0) {
        s.player.priv = Math.min(s.player.privMax, s.player.priv + DEVICE.zombie.priv);
        s.stats.zombiesReaped += 1;
        ev.push({ t: "zombie-reaped", x: z.x, y: z.y });
        continue; // reaped; the corpse is gone
      }
      next.push({ ...z, reap });
      continue;
    }
    // Step away and the dwell bleeds back, at half rate. Leaving is
    // cheaper than arriving, but it is not free — otherwise the optimal
    // play is to nibble at it between dodges at no cost.
    if (z.reap < DEVICE.zombie.reap && s.tick % DEVICE.zombie.decayEvery === 0) {
      next.push({ ...z, reap: z.reap + 1 });
      continue;
    }
    next.push(z);
  }
  s.zombies = next;
}

/**
 * runlevel 2 — the arena closes in.
 *
 * Space is the correct difficulty axis for a cursor. A mouse out-runs
 * bullet speed trivially — there is no acceleration to fight and no
 * turn radius — so making bullets faster mostly makes them easier to
 * see coming. Making the room smaller cannot be out-run by anything.
 *
 * The scale eases toward its target and is floored, because a room too
 * small to survive a ring is not a difficulty setting.
 */
function shrinkDevice(ctx: Ctx): void {
  const { s, ev } = ctx;
  const target = arenaTarget(s.phaseIndex, phaseProgress(s));
  const before = s.arenaScale;
  let scale = before;
  if (scale > target) scale = Math.max(target, scale - DEVICE.shrink.rate);
  else if (scale < target) scale = Math.min(target, scale + DEVICE.shrink.rate);
  scale = clamp(scale, DEVICE.shrink.min, 1);
  s.arenaScale = scale;
  ctx.arena = arenaBounds(scale);

  const q = (v: number): number => Math.round(v / DEVICE.shrink.step);
  if (q(scale) !== q(before)) ev.push({ t: "shrink", scale });
}

/**
 * DAEMON patrols. Patterns are aimed from its position, so a fixed
 * origin makes every card read the same way twice; a moving one makes
 * the same authored card a different problem depending on where in the
 * loop it fires. The loop is seeded and closed — learnable within a
 * run, different between runs — rather than a wander, because a boss
 * you cannot predict is a boss you cannot practise.
 */
function moveDaemon(ctx: Ctx): void {
  const { s } = ctx;
  const p = daemonPos(s.config.seed, s.tick, s.phaseIndex, ctx.arena);
  s.daemon.x = p.x;
  s.daemon.y = p.y;
}

/** Nothing that has a position is allowed to be outside the live arena
 *  once the walls have moved — least of all the player, who would
 *  otherwise be standing in a region the game no longer draws or
 *  defends. Children and corpses come in too, so that a shrink can
 *  never strand a reap out of reach. */
function clampActors(ctx: Ctx): void {
  const { s, arena } = ctx;

  s.player.x = clamp(s.player.x, arena.x0 + PLAYER_R, arena.x1 - PLAYER_R);
  s.player.y = clamp(s.player.y, arena.y0 + PLAYER_R, arena.y1 - PLAYER_R);

  const inset = 30;
  const fixX = (v: number): number => clamp(v, arena.x0 + inset, arena.x1 - inset);
  const fixY = (v: number): number => clamp(v, arena.y0 + inset, arena.y1 - inset);

  if (s.children.some((c) => c.x !== fixX(c.x) || c.y !== fixY(c.y))) {
    s.children = s.children.map((c) => ({ ...c, x: fixX(c.x), y: fixY(c.y) }));
  }
  if (s.zombies.some((z) => z.x !== fixX(z.x) || z.y !== fixY(z.y))) {
    s.zombies = s.zombies.map((z) => ({ ...z, x: fixX(z.x), y: fixY(z.y) }));
  }
}

function currentPattern(s: SimState): Pattern | undefined {
  const card = s.cardPlan[s.phaseIndex]?.[s.cardIndex];
  if (!card) return PATTERNS[0];
  // Looked up by id, not position. `phases.ts` throws at module load if
  // a card names a pattern that doesn't exist, so a miss here means the
  // table changed underneath a saved state rather than a typo.
  return patternById(card.pattern) ?? PATTERNS[0];
}

function runPattern(ctx: Ctx): void {
  const { s, rng } = ctx;
  const pattern = currentPattern(s);
  if (!pattern) return;

  const scale = niceScale(s.config.nice);
  const local = s.daemon.patternClock % pattern.loop;

  for (const st of pattern.steps) {
    if (st.at !== local) continue;
    const bullets = emit(
      {
        ox: s.daemon.x,
        oy: s.daemon.y,
        px: s.player.x,
        py: s.player.y,
        pvx: s.player.x - ctx.prev.x,
        pvy: s.player.y - ctx.prev.y,
        tick: s.daemon.patternClock,
        rng,
        nextId: () => s.nextId++,
      },
      { ...st, speed: mod(s, "bullet-speed", st.speed * scale.bulletSpeed) },
    );
    s.bullets = s.bullets.concat(bullets);
  }
}

function moveBullets(ctx: Ctx): void {
  const { s, arena } = ctx;
  const next: Bullet[] = [];
  for (const b of s.bullets) {
    // Bouncing is measured against the *live* wall, not the logical
    // one, or a bounce during runlevel 2 would reflect off a wall that
    // is no longer there and leave the bullet in the void. Bullets that
    // start outside the live arena (a `wall` emitter spawns on the
    // logical edge) are left alone until they have entered it, so
    // shrinking never eats a pattern at birth.
    const wasInside =
      b.x >= arena.x0 && b.x <= arena.x1 && b.y >= arena.y0 && b.y <= arena.y1;
    if (b.delay > 0) {
      next.push({ ...b, delay: b.delay - 1, ttl: b.ttl - 1 });
      continue;
    }
    let { x, y, vx, vy, bounces } = b;

    if (b.homing !== 0) {
      const want = angleT(x, y, s.player.x, s.player.y);
      const have = angleT(0, 0, vx, vy);
      const d = deltaT(have, want);
      const turn = clamp(d, -b.homing, b.homing);
      const speed = Math.hypot(vx, vy);
      const dir = dirT(have + turn);
      vx = dir.x * speed;
      vy = dir.y * speed;
    }

    if (b.accel !== 1) {
      vx *= b.accel;
      vy *= b.accel;
    }

    x += vx;
    y += vy;

    if (bounces > 0 && wasInside) {
      if (x < arena.x0 || x > arena.x1) {
        vx = -vx;
        x = clamp(x, arena.x0, arena.x1);
        bounces -= 1;
      }
      if (y < arena.y0 || y > arena.y1) {
        vy = -vy;
        y = clamp(y, arena.y0, arena.y1);
        bounces -= 1;
      }
    }

    const ttl = b.ttl - 1;
    const margin = 80;
    const gone =
      ttl <= 0 ||
      x < -margin ||
      x > ARENA_W + margin ||
      y < -margin ||
      y > ARENA_H + margin;
    if (!gone) next.push({ ...b, x, y, vx, vy, ttl, bounces });
  }
  s.bullets = next;
}

function resolveGrazeAndHits(ctx: Ctx): void {
  const { s, ev } = ctx;
  const px = s.player.x;
  const py = s.player.y;
  // Resolved once per tick rather than per bullet: the value cannot
  // change mid-loop, and grazing is the hottest path in the sim.
  const grazeR = mod(s, "graze-radius", GRAZE_R);
  const survivors: Bullet[] = [];

  for (const b of s.bullets) {
    const d = dist(px, py, b.x, b.y);

    // Hit first — a bullet that connects cannot also graze.
    if (d < b.r + PLAYER_R) {
      if (s.player.iframes > 0) {
        survivors.push(b);
        continue;
      }
      const taken = modInt(s, "damage-taken", b.damage);
      s.player.hp = Math.max(0, s.player.hp - taken);
      // Courage is not bankable through a hit — unless an upgrade says
      // otherwise. With nothing held this is exactly the old `= 0`.
      s.player.priv = privAfterHit(s.upgrades, s.player.priv);
      // The whole point of the mechanic. Everything the danger bought
      // is gone in one touch, which is what makes holding a high load
      // feel like holding something.
      s.player.loadMilli = LOAD_BASE;
      s.player.iframes = modInt(s, "iframes", IFRAMES);
      s.stats.hitsTaken += 1;
      ev.push({ t: "player-hit", x: px, y: py, damage: taken });
      continue; // the bullet is consumed
    }

    // Graze — the whole reason the cursor's agility isn't a problem.
    // Without it, the optimal play is to cower in a corner.
    if (!b.grazed && d < b.r + PLAYER_R + grazeR) {
      const gain = modInt(s, "graze-priv", PRIV_PER_GRAZE);
      const before = s.player.priv;
      const after = Math.min(s.player.privMax, before + gain);
      // What the cap threw away. Previously invisible — the value was
      // simply clamped off and gone.
      const overflow = before + gain - after;
      s.player.priv = after;
      s.stats.grazes += 1;

      // Scored at the load you had *arriving* at this bullet, then the
      // load rises. Paying first is what makes the curve reward staying
      // rather than reward the first contact.
      const load = s.player.loadMilli;
      s.stats.score += Math.floor((SCORE_PER_GRAZE * load) / 1000);
      if (overflow > 0) {
        s.stats.score += Math.floor((SCORE_PER_OVERFLOW * overflow * load) / 1000);
      }
      s.player.loadMilli = Math.min(LOAD_CAP, load + LOAD_PER_GRAZE);
      if (s.player.loadMilli > s.stats.peakLoadMilli) {
        s.stats.peakLoadMilli = s.player.loadMilli;
      }

      ev.push({ t: "graze", x: b.x, y: b.y });
      survivors.push({ ...b, grazed: true });
      continue;
    }

    survivors.push(b);
  }

  s.bullets = survivors;
}

/**
 * Age the warnings, and hatch the ones that were promises.
 *
 * A `spawn` telegraph *is* the pending fork: it carries the position,
 * and the child appears when it expires. Scheduling the spawn
 * separately would let the two disagree — the phase clock can leap when
 * a card is cleared on HP, and a child that arrives somewhere the ring
 * never pointed at is exactly the unfairness the telegraph exists to
 * prevent.
 */
function tickTelegraphs(ctx: Ctx): void {
  const { s, ev, rng } = ctx;
  const kept: SimState["telegraphs"] = [];
  const hatched: SimState["telegraphs"] = [];

  for (const t of s.telegraphs) {
    const ticks = t.ticks - 1;
    if (ticks > 0) {
      kept.push({ ...t, ticks });
      continue;
    }
    if (t.kind === "spawn") hatched.push(t);
  }
  s.telegraphs = kept;

  if (hatched.length === 0) return;
  // A fork that was warned about while the device was live still lands
  // even if the phase has ticked over in the meantime — except at
  // runlevel 1, where init has already reaped everything.
  if (deviceOn(s.phaseIndex, "single-user")) return;

  const born: Child[] = hatched.map((t) => ({
    id: s.nextId++,
    x: t.x,
    y: t.y,
    hp: DEVICE.fork.hp,
    hpMax: DEVICE.fork.hp,
    pattern: childPatternIndex(rng),
    // Silent for its first cadence. A child that fires the instant it
    // materialises is a spawn-kill with extra steps.
    cooldown: DEVICE.fork.emitEvery,
  }));
  s.children = s.children.concat(born);
  ev.push({ t: "fork", count: born.length });
}

/**
 * The card → card → phase → draft → phase → win lifecycle.
 *
 * A card ends on HP or on its timer, whichever comes first. The last
 * card of a phase ends the phase; the last phase ends the run. Between
 * phases the sim *stops* and waits in `draft` — see `resolveDraft`,
 * which is the only way forward, because picking an upgrade is a
 * decision and decisions are input, not simulation.
 */
function advanceCard(ctx: Ctx): void {
  const { s, ev } = ctx;
  const cards = s.cardPlan[s.phaseIndex];
  if (!cards || cards.length === 0) return;
  const card = cards[s.cardIndex];
  if (!card) return;

  const hpFloor = phaseHpFloor(s, s.cardIndex + 1);
  const cleared = s.daemon.hp <= hpFloor || s.cardTicks <= 0;
  if (!cleared) return;

  ev.push({ t: "card-clear", name: card.name, ticks: s.tick });
  s.stats.cardsCleared += 1;
  // The board clears between cards, but the *children do not*. An
  // orphan outlives the card that forked it and outlives the phase
  // that forked it, which is the entire weight of the fork decision.
  s.bullets = [];

  if (s.cardIndex + 1 < cards.length) {
    s.cardIndex += 1;
    const nextCard = cards[s.cardIndex]!;
    s.cardTicks = nextCard.timeout;
    ev.push({ t: "card-start", name: nextCard.name });
    // Clearing a card early skips the rest of its timer, and the phase
    // clock jumps with it. Walk the devices over the skipped interval
    // so a fork due inside it still lands.
    scheduleDevices(ctx, ctx.clock, phaseClock(s));
    return;
  }

  ev.push({ t: "phase-clear", phase: phaseAt(s.phaseIndex).id, ticks: s.tick });

  if (s.phaseIndex + 1 >= s.cardPlan.length) {
    // Runlevel 1 cleared. Sixty seconds with no verbs, survived.
    s.status = "won";
    ev.push({ t: "won", ticks: s.tick });
    return;
  }

  // Phase over — hand off to the draft. The sim does not *pick* the
  // upgrade, but it does roll the offer, from its own seeded generator,
  // so that the draft screen is part of the replay rather than a
  // wall-clock decision made outside it.
  s.status = "draft";
  s.offers = rollOffers(s.upgrades, ctx.rng);
}

/**
 * Begin a phase. Everything transient resets; everything the player
 * has done to themselves does not.
 */
function enterPhase(s: SimState, index: number, ev: SimEvent[]): void {
  s.phaseIndex = index;
  s.cardIndex = 0;
  s.cardTicks = s.cardPlan[index]?.[0]?.timeout ?? 0;
  s.bullets = [];
  s.telegraphs = [];
  s.daemon = { ...s.daemon, cooldown: 0, step: 0, guarded: false };

  const phase = phaseAt(index);
  if (phase.device === "single-user") {
    // init reaps everything on the way down. This is also the only
    // reason the last phase is survivable: orphans you chose not to
    // kill would otherwise follow you into a phase with no signals to
    // kill them with, which is a punishment with no counterplay.
    for (const c of s.children) ev.push({ t: "child-down", x: c.x, y: c.y });
    s.children = [];
    s.zombies = [];
  }

  ev.push({ t: "phase-start", phase: phase.id, nice: phase.nice });
  const card = s.cardPlan[index]?.[0];
  if (card) ev.push({ t: "card-start", name: card.name });
}

/**
 * Resolve the draft and start the next phase.
 *
 * The run controller calls this when the player picks (or skips, with
 * no id). It is the same shape as `step`: pure, non-mutating, and its
 * events are drained by the presentation layer exactly like a tick's.
 * A replay records the picked id alongside the input stream and calls
 * this at the same tick, which is all the draft needs to be replayable.
 */
export function resolveDraft(prevState: SimState, pick?: string): SimState {
  if (prevState.status !== "draft") return prevState;

  const s: SimState = {
    ...prevState,
    player: { ...prevState.player, cooldowns: prevState.player.cooldowns.slice() },
    daemon: { ...prevState.daemon },
    stats: { ...prevState.stats },
    events: [],
  };

  if (pick && prevState.offers.includes(pick) && !prevState.upgrades.includes(pick)) {
    s.upgrades = prevState.upgrades.concat(pick);
  }
  s.offers = [];

  const next = prevState.phaseIndex + 1;
  if (next >= s.cardPlan.length) {
    s.status = "won";
    s.events.push({ t: "won", ticks: s.tick });
    return s;
  }

  s.status = "running";
  enterPhase(s, next, s.events);
  return s;
}

/** HP threshold at which card `n` of the current phase is considered
 *  cleared. Cards divide their phase's HP band by `hpShare`. */
function phaseHpFloor(s: SimState, cardsDone: number): number {
  const cards = s.cardPlan[s.phaseIndex] ?? [];
  const phases = Math.max(1, s.cardPlan.length);
  const band = s.daemon.hpMax / phases;
  const phaseTop = s.daemon.hpMax - band * s.phaseIndex;
  let share = 0;
  for (let i = 0; i < cardsDone && i < cards.length; i++) share += cards[i]!.hpShare;
  return Math.max(0, phaseTop - band * share);
}

function checkEnd(ctx: Ctx): void {
  const { s, ev } = ctx;
  if (s.status === "won" || s.status === "lost") return;
  // DAEMON at zero ends the run wherever it happens, including out from
  // under a draft that the same tick's card clear had just queued up.
  if (s.daemon.hp <= 0) {
    s.status = "won";
    ev.push({ t: "won", ticks: s.tick });
    return;
  }
  if (s.player.hp <= 0) {
    s.status = "lost";
    ev.push({ t: "player-down" });
    ev.push({ t: "lost", ticks: s.tick });
  }
}

// ── Helpers for the shell ─────────────────────────────────────────────

/** Run time as a display string. Ticks, not wall-clock — this is IGT,
 *  immune to frame drops and background-tab throttling. */
export function formatTicks(ticks: number): string {
  const total = ticks / TICK_HZ;
  const m = Math.floor(total / 60);
  const sec = total - m * 60;
  return `${m}:${sec.toFixed(2).padStart(5, "0")}`;
}

export { NO_SPAWN_R, GRAZE_R, PLAYER_R };
